/* global axios */

// WAHA session management — proxied through the Rails backend so the WAHA
// admin_token (server-wide super-user) never reaches the browser. The Rails
// controller resolves the session name and base URL from the inbox itself,
// so the frontend only has to pass (accountId, inboxId).

const sessionUrl = (accountId, inboxId, suffix = '') =>
  `/api/v1/accounts/${accountId}/inboxes/${inboxId}/waha_session${suffix}`;

// Logger explicito para diagnostico no console do navegador. Toda chamada
// WAHA loga sucesso (debug) e falha (error) com URL, status HTTP, status do
// upstream WAHA (quando o backend repassa) e o body do erro.
const logWahaCall = (method, url, result) => {
  const tag = '[WAHA API]';
  if (result.ok) {
    // eslint-disable-next-line no-console
    console.debug(`${tag} ${method} ${url} OK`, result.data);
  } else {
    // eslint-disable-next-line no-console
    console.error(`${tag} ${method} ${url} FAILED`, {
      httpStatus: result.httpStatus,
      upstreamStatus: result.upstreamStatus,
      backendError: result.backendError,
      networkMessage: result.networkMessage,
      data: result.data,
    });
  }
};

const handleAxiosError = (error, method, url, fallback) => {
  const data = error.response?.data;
  const result = {
    ok: false,
    httpStatus: error.response?.status,
    upstreamStatus: data?.upstream_status,
    backendError: data?.error,
    networkMessage: error.message,
    data,
  };
  logWahaCall(method, url, result);
  const message = data?.error || data?.message;
  const err = new Error(message || fallback);
  err.httpStatus = result.httpStatus;
  err.upstreamStatus = result.upstreamStatus;
  err.backendError = result.backendError;
  err.payload = data;
  throw err;
};

class WahaAPI {
  // Fetch the current WAHA session and its connection status.
  // Returns: { session: { name, status, ... } | null, status: string | null }
  static async fetchStatus(accountId, inboxId) {
    const url = sessionUrl(accountId, inboxId, '/status');
    try {
      const { data } = await axios.get(url);
      logWahaCall('GET', url, { ok: true, data });
      return data;
    } catch (error) {
      handleAxiosError(
        error,
        'GET',
        url,
        'Failed to fetch Waha session status'
      );
      return undefined;
    }
  }

  // Fetch the QR code payload from WAHA. Returns the WAHA response body
  // as-is (string base64 or { base64, code, qrcode, ... }) so the existing
  // normalizeQrPayload helper in the Vue layer keeps working.
  static async fetchQrCode(accountId, inboxId) {
    const url = sessionUrl(accountId, inboxId, '/qr');
    try {
      const { data } = await axios.get(url);
      logWahaCall('GET', url, { ok: true, data });
      return data;
    } catch (error) {
      handleAxiosError(error, 'GET', url, 'Failed to fetch Waha QR code');
      return undefined;
    }
  }

  static async restartSession(accountId, inboxId) {
    const url = sessionUrl(accountId, inboxId, '/restart');
    try {
      const { data } = await axios.post(url);
      logWahaCall('POST', url, { ok: true, data });
      return data;
    } catch (error) {
      handleAxiosError(error, 'POST', url, 'Failed to restart Waha session');
      return undefined;
    }
  }

  static async stopSession(accountId, inboxId, logout = false) {
    const url = sessionUrl(accountId, inboxId, '/stop');
    try {
      const { data } = await axios.post(url, { logout });
      logWahaCall('POST', url, { ok: true, data });
      return data;
    } catch (error) {
      handleAxiosError(error, 'POST', url, 'Failed to stop Waha session');
      return undefined;
    }
  }

  static async logoutSession(accountId, inboxId) {
    const url = sessionUrl(accountId, inboxId, '/logout');
    try {
      const { data } = await axios.post(url);
      logWahaCall('POST', url, { ok: true, data });
      return data;
    } catch (error) {
      handleAxiosError(error, 'POST', url, 'Failed to log out Waha session');
      return undefined;
    }
  }

  static async deleteSession(accountId, inboxId) {
    const url = sessionUrl(accountId, inboxId);
    try {
      const { data } = await axios.delete(url);
      logWahaCall('DELETE', url, { ok: true, data });
      return data;
    } catch (error) {
      handleAxiosError(error, 'DELETE', url, 'Failed to delete Waha session');
      return undefined;
    }
  }

  // ==========================================
  // Chatwoot WhatsApp Controller Integration
  // These methods call Chatwoot's WhatsApp controller endpoints
  // which internally communicate with WAHA or Evolution API
  // ==========================================

  static buildChatwootUrl(accountId, inboxId, action) {
    return `/api/v1/accounts/${accountId}/inboxes/${inboxId}/whatsapp/${action}`;
  }

  // Check if phone numbers exist on WhatsApp
  static async checkNumber(accountId, inboxId, numbers) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'check_number');
    try {
      const response = await axios.post(url, {
        numbers: Array.isArray(numbers) ? numbers : [numbers],
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to check phone numbers'
      );
    }
  }

  // Send a reaction emoji to a message
  static async sendReaction(accountId, inboxId, messageId, emoji) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_reaction');
    try {
      const response = await axios.post(url, {
        message_id: messageId,
        emoji,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send reaction');
    }
  }

  // Send a poll/survey message
  static async sendPoll(
    accountId,
    inboxId,
    conversationId,
    question,
    options,
    maxAnswers = 1
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_poll');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        question,
        options,
        max_answers: maxAnswers,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send poll');
    }
  }

  // Send a location message
  static async sendLocation(
    accountId,
    inboxId,
    conversationId,
    { latitude, longitude, name, address }
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_location');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        latitude,
        longitude,
        name,
        address,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send location');
    }
  }

  // Send a contact/vCard message
  static async sendContact(accountId, inboxId, conversationId, contact) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_contact');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        contact,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send contact');
    }
  }

  // Send a sticker message
  static async sendSticker(accountId, inboxId, conversationId, stickerUrl) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_sticker');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        sticker_url: stickerUrl,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send sticker');
    }
  }

  // Get WhatsApp user information
  static async getUserInfo(accountId, inboxId, numbers) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'get_user_info');
    try {
      const response = await axios.post(url, {
        numbers: Array.isArray(numbers) ? numbers : [numbers],
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get user info');
    }
  }

  // Get WhatsApp user avatar URL
  static async getAvatar(accountId, inboxId, number, preview = false) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'get_avatar');
    try {
      const response = await axios.post(url, {
        number,
        preview,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get avatar');
    }
  }

  // Send typing indicator
  static async sendTyping(
    accountId,
    inboxId,
    conversationId,
    state = 'typing'
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_typing');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        state,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to send typing indicator'
      );
    }
  }

  // Send presence status
  static async sendPresence(
    accountId,
    inboxId,
    conversationId,
    state,
    isAudio = false
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_presence');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        state,
        is_audio: isAudio,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to send presence');
    }
  }

  // Forward a message to another conversation
  static async forwardMessage(accountId, inboxId, messageId, toConversationId) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'forward_message');
    try {
      const response = await axios.post(url, {
        message_id: messageId,
        to_conversation_id: toConversationId,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to forward message'
      );
    }
  }

  // Star or unstar a message
  static async starMessage(accountId, inboxId, messageId, star = true) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'star_message');
    try {
      const response = await axios.post(url, {
        message_id: messageId,
        star,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to update message star'
      );
    }
  }

  // Block a contact
  static async blockContact(
    accountId,
    inboxId,
    { conversationId, contactId } = {}
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'block_contact');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        contact_id: contactId,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to block contact');
    }
  }

  // Unblock a contact
  static async unblockContact(
    accountId,
    inboxId,
    { conversationId, contactId } = {}
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'unblock_contact');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        contact_id: contactId,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to unblock contact'
      );
    }
  }

  // Reject an incoming call
  static async rejectCall(accountId, inboxId, callId) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'reject_call');
    try {
      const response = await axios.post(url, {
        call_id: callId,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to reject call');
    }
  }

  // Mark messages as read
  static async markRead(accountId, inboxId, conversationId, messageIds = null) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'mark_read');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        message_ids: messageIds,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to mark messages as read'
      );
    }
  }

  // Archive or unarchive a chat
  static async archiveChat(accountId, inboxId, conversationId, archive = true) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'archive_chat');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        archive,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to archive chat');
    }
  }

  // Get list of blocked contacts
  static async getBlockedContacts(accountId, inboxId) {
    const url = this.buildChatwootUrl(
      accountId,
      inboxId,
      'get_blocked_contacts'
    );
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to get blocked contacts'
      );
    }
  }

  // Send a message with custom link preview
  static async sendLinkPreview(
    accountId,
    inboxId,
    conversationId,
    { text, previewUrl, title, description, imageUrl }
  ) {
    const url = this.buildChatwootUrl(accountId, inboxId, 'send_link_preview');
    try {
      const response = await axios.post(url, {
        conversation_id: conversationId,
        text,
        preview_url: previewUrl,
        title,
        description,
        image_url: imageUrl,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Failed to send link preview'
      );
    }
  }
}

export default WahaAPI;
